import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState, type ReactNode, type UIEvent } from 'react';
import { computeVisibleRange, isNearEnd, totalSize } from './virtual';
import type { VirtualHandle } from './VirtualList';

export interface VirtualGridProps<T> {
  items: readonly T[];
  /** Tiles per row — the caller computes this with `computeColumns()` from a measured container
   * width, matching the CSS `repeat(auto-fill, minmax(...))` formula exactly (virtual.ts). */
  columns: number;
  /** Height of one row of tiles in px, excluding `gap` (DESIGN.md §4.2 — never measured at
   * runtime; the caller derives it once per resize from the fluid thumbnail's aspect ratio plus
   * the fixed `--tile-meta-h`, not during scroll). */
  rowHeight: number;
  /** Row and column gap in px — pass `gridGap` from `web/src/design/constants.ts` (mirrors
   * `--space-3`; see that file's own comment on why this is a JS constant, not a token read). */
  gap: number;
  renderItem: (item: T, index: number) => ReactNode;
  getKey: (item: T, index: number) => string | number;
  /** Extra ROWS rendered before/after the viewport (compare `VirtualList`'s `overscan`, which is
   * in items — a grid's unit of virtualisation is the row). */
  overscan?: number;
  className?: string;
  onVisibleIndexChange?: (index: number) => void;
  /** MVP-230: same contract as `VirtualList`'s — see its doc comment. */
  onEndReached?: () => void;
  /** How many ROWS from the end counts as "near" (compare `VirtualList`'s, which is in items —
   * a grid's unit of virtualisation is the row). Default 2 = roughly one screen of tiles. */
  endThreshold?: number;
}

function VirtualGridInner<T>(
  { items, columns, rowHeight, gap, renderItem, getKey, overscan = 2, className, onVisibleIndexChange, onEndReached, endThreshold = 2 }: VirtualGridProps<T>,
  ref: React.ForwardedRef<VirtualHandle>
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);
  const lastReported = useRef<number | null>(null);

  const cols = Math.max(1, Math.floor(columns));
  const rowStride = rowHeight + gap;
  const rowCount = Math.max(0, Math.ceil(items.length / cols));

  // Measured on resize only, never on scroll — same rule VirtualList follows (DESIGN.md §4.2).
  //
  // MVP-670: zeroes are refused, for the reason `useMeasuredWidth` (LibraryScreen.tsx) gives at
  // length — a `display:none` ancestor makes ResizeObserver report `0x0`, and that is an absence of
  // a layout box rather than a measurement. Here it would empty the rendered range (a zero viewport
  // shows no rows) and then refill it on the way back, which is work and a paint for a screen
  // nobody is looking at. The caller's own guard is the one that protects the scroll offset; this
  // one keeps a hidden grid from churning.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    if (el.clientHeight > 0) setViewportHeight(el.clientHeight);
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry && entry.contentRect.height > 0) setViewportHeight(entry.contentRect.height);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      scrollToIndex(index: number, align: 'nearest' | 'start' = 'nearest') {
        const el = containerRef.current;
        if (!el) return;
        const row = Math.floor(index / cols);
        const top = row * rowStride;
        const bottom = top + rowHeight;
        if (align === 'start') el.scrollTop = top;
        else if (top < el.scrollTop) el.scrollTop = top;
        else if (bottom > el.scrollTop + el.clientHeight) el.scrollTop = bottom - el.clientHeight;
      },
    }),
    [cols, rowStride, rowHeight]
  );

  const range = useMemo(
    () => computeVisibleRange(scrollTop, viewportHeight, rowCount, rowStride, overscan),
    [scrollTop, viewportHeight, rowCount, rowStride, overscan]
  );

  // Ref-read for the same reason VirtualList does it: an inline arrow from the caller must not
  // re-arm the effect on every render.
  const endReachedRef = useRef(onEndReached);
  endReachedRef.current = onEndReached;
  useEffect(() => {
    if (isNearEnd(range.endIndex, rowCount, endThreshold)) endReachedRef.current?.();
  }, [range.endIndex, rowCount, endThreshold]);

  function handleScroll(e: UIEvent<HTMLDivElement>) {
    const top = e.currentTarget.scrollTop;
    setScrollTop(top);
    if (onVisibleIndexChange) {
      const visibleRow = Math.min(rowCount - 1, Math.max(0, Math.floor(top / rowStride)));
      const visibleIndex = visibleRow * cols;
      if (visibleIndex !== lastReported.current) {
        lastReported.current = visibleIndex;
        onVisibleIndexChange(visibleIndex);
      }
    }
  }

  const rows: Array<{ rowIndex: number; start: number; end: number }> = [];
  for (let r = range.startIndex; r < range.endIndex; r += 1) {
    const start = r * cols;
    rows.push({ rowIndex: r, start, end: Math.min(items.length, start + cols) });
  }

  return (
    <div
      ref={containerRef}
      className={['virtual-scroll', className].filter(Boolean).join(' ')}
      onScroll={handleScroll}
      data-testid="virtual-grid"
    >
      <div className="virtual-scroll__spacer" style={{ height: totalSize(rowCount, rowStride) }}>
        <div className="virtual-scroll__window" style={{ transform: `translateY(${range.offsetTop}px)` }}>
          {rows.map(({ rowIndex, start, end }) => (
            <div
              key={rowIndex}
              className="virtual-scroll__row"
              // MVP-295: `minmax(0, 1fr)`, not a bare `1fr`. A bare `1fr` is `minmax(auto, 1fr)`, and
              // that `auto` floor lets a tile whose content reports a wide intrinsic size push its own
              // track past its fair share — the extra width comes straight off its neighbours, so one
              // tile in the row rendered ~1.7x too wide (and, via the thumbnail's aspect-ratio, too
              // tall for the fixed `rowHeight`, clipping its own title) while the rest were squeezed.
              // The `0` floor says a track is never obliged to fit its content, which is what an
              // even grid means. `overflow: clip` on `.virtual-scroll__item` does NOT cover this.
              style={{ height: rowHeight, marginBottom: gap, gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
            >
              {items.slice(start, end).map((item, i) => {
                const index = start + i;
                return (
                  <div key={getKey(item, index)} className="virtual-scroll__item" style={{ height: rowHeight }}>
                    {renderItem(item, index)}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** DESIGN.md §9.2 `VirtualGrid` — the grid-view sibling of `VirtualList`. Same constant-DOM-node
 * guarantee (AC-UI-1), one row of real DOM per rendered row rather than per tile. */
export const VirtualGrid = forwardRef(VirtualGridInner) as <T>(
  props: VirtualGridProps<T> & { ref?: React.ForwardedRef<VirtualHandle> }
) => ReturnType<typeof VirtualGridInner>;
